import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useUser } from './UserContext';
import { useTenant } from './TenantContext';
import {
  deleteNotification,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  subscribeNotifications,
} from '../platform/notifications/notificationsService';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { user } = useUser();
  const { tenantId } = useTenant();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid || !tenantId) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    return subscribeNotifications(
      user.uid,
      tenantId,
      (items) => {
        setNotifications(items);
        setLoading(false);
      },
      (error) => {
        console.error('[notifications] Failed to subscribe', error);
        setLoading(false);
      },
    );
  }, [user?.uid, tenantId]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.readAt).length,
    [notifications],
  );

  const markRead = useCallback(async (notificationId) => {
    if (!user?.uid) return;
    await markNotificationAsRead(user.uid, notificationId);
  }, [user?.uid]);

  const markAllRead = useCallback(async () => {
    if (!user?.uid) return;
    await markAllNotificationsAsRead(user.uid, notifications);
  }, [notifications, user?.uid]);

  const remove = useCallback(async (notificationId) => {
    if (!user?.uid) return;
    await deleteNotification(user.uid, notificationId);
  }, [user?.uid]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    remove,
  }), [notifications, unreadCount, loading, markRead, markAllRead, remove]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
}
